<template>
  <div class="container">
    <h1 style="font-size: 20px">{{ $t("restaurants") }}</h1>
    <v-row>
      <v-col cols="auto">
        <text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('search')"
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <autocomplete
          :items="filteredCountries"
          item-value="id"
          item-text="name"
          v-model="country"
          :label="$t('country')"
          return-object
        />
      </v-col>
      <v-col cols="auto">
        <autocomplete
          :items="country?.cities || []"
          :disabled="!country"
          item-value="id"
          item-text="name"
          return-object
          v-model="city"
          :label="$t('city')"
        />
      </v-col>
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading" class="align-content-center justify-content-center mt-12">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          :size="70"
          :width="7"
          color="primary"
          indeterminate
        ></v-progress-circular>
        <p class="mt-4 text-h6 grey--text">{{ $t("loading") }}...</p>
      </v-col>
    </v-row>

    <!-- No Data State -->
    <v-row
      v-else-if="!loading && (!filteredRestaurants || filteredRestaurants.length === 0)"
      class="mt-12"
    >
      <v-col cols="12" class="text-center">
        <v-icon size="100" color="grey lighten-1">mdi-store-off-outline</v-icon>
        <p class="mt-4 text-h6 grey--text">{{ $t("no_restaurants_found") }}</p>
      </v-col>
    </v-row>

    <!-- Restaurants List -->
    <v-row v-else class="align-content-center justify-content-center">
      <v-col
        cols="12"
        sm="6"
        md="4"
        lg="3"
        v-for="res in filteredRestaurants"
        :key="res.id"
      >
        <v-card>
          <v-img
            @click="selectRes(res)"
            :aspect-ratio="16 / 9"
            :src="getRestaurantLogo(res)"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular
                  indeterminate
                  color="grey lighten-5"
                ></v-progress-circular>
              </v-row>
            </template>
            <v-row
              no-gutters
              align="center"
              justify="center"
              class="fill-height"
              v-if="!getRestaurantLogo(res)"
            >
              <v-col cols="12" class="text-center">
                <v-icon size="64px" color="grey lighten-1">mdi-storefront</v-icon>
              </v-col>
            </v-row>
          </v-img>
          <v-divider></v-divider>
          <v-card-title>{{ toLatin(res.name) }}</v-card-title>
          <v-card-text>
            <div v-if="res.address" class="d-flex align-center mb-2">
              <v-icon small color="grey darken-1" class="mr-2">mdi-map-marker</v-icon>
              <span class="body-2">{{ toLatin(res.address) }}</span>
            </div>
            <div v-if="res.city" class="d-flex align-center">
              <v-icon small color="grey darken-1" class="mr-2">mdi-city</v-icon>
              <span class="body-2 font-weight-bold">{{ toLatin(res.city.name) }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import conf from "@/config";
import restaurantService from "@/services/restaurant";

export default {
  name: "RestoraniGuest",
  data() {
    return {
      search: "",
      restaurants: [],
      countries: [],
      cities: [],
      country: null,
      city: null,
      loading: false,
    };
  },
  created() {
    this.getCountries();
    this.getRestaurants();
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    filteredCities() {
      return this.cities.filter(Boolean);
    },
    filteredCountries() {
      return this.countries;
    },
    filteredRestaurants() {
      let filtered = this.restaurants;

      if (this.search) {
        const searchLower = this.translate(this.search.toLowerCase());
        filtered = filtered.filter((res) =>
          this.translate(res.name.toLowerCase()).includes(searchLower)
        );
      }

      if (this.country) {
        filtered = filtered.filter((res) => res.city?.country.id === this.country.id);
      }

      if (this.city) {
        filtered = filtered.filter((res) => res.city?.id === this.city.id);
      }

      return filtered;
    },
  },
  methods: {
    getCountries() {
      this.loading = true;
      restaurantService
        .getCountriesAll()
        .then(({ data }) => {
          this.countries = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getRestaurants() {
      this.loading = true;
      restaurantService
        .getAllRestaurantsPublic()
        .then(({ data }) => {
          this.restaurants = data.data;
        })
        .catch((error) => {
          console.error("Error loading restaurants:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getRestaurantLogo(restaurant) {
      if (!restaurant?.settings?.logo) {
        return null;
      }
      return `${conf.apiUrlBase}/gallery/${restaurant.id}/${restaurant.settings.logo}`;
    },
    selectRes(res = null) {
      localStorage.removeItem("newRes");
      if (res == null && this.restaurant) {
        restaurantService
          .chooseRestaurant(this.user.id, this.restaurant.id)
          .then(({ data }) => {
            localStorage.setItem("guest", data);
            localStorage.setItem("newRes", this.restaurant.id);
            localStorage.setItem("newCompany", JSON.stringify(data.company));
            this.$root.$emit("setRestoranId", data.data);
            this.deliveryRestoran = data.data;
            this.$router.push({ name: "guest", params: { data: data.data } });
          });
      } else {
        if (res) {
          restaurantService
            .chooseRestaurant(this.user ? this.user.id : "", res.id)
            .then(({ data }) => {
              localStorage.setItem("guest", JSON.stringify(data));
              localStorage.setItem("newRes", JSON.stringify(data.data));
              this.$root.$emit("test", JSON.stringify(data.data));
              localStorage.setItem("newCompany", JSON.stringify(data.company));
              this.$root.$emit("setRestoranId", data.data);
              this.dialogRestoran = false;
              this.$router.push({ name: "guest", params: { data: data.data } });
            });
        }
      }
    },
  },
};
</script>

<style scoped></style>
